import React, { useState, useRef, useEffect } from 'react';
import { useStyles } from '../../contexts/StyleContext';
import { X } from 'lucide-react';

const MultiSelect = ({
  label,
  error,
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  className = '',
  ...props
}) => {
  const styles = useStyles();
  const { form } = styles;
  
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const containerClasses = `
    relative 
    w-full 
    mb-4
  `.trim();

  const selectClasses = `
    w-full 
    px-4 
    py-3 
    rounded-lg 
    border-2 
    bg-white 
    transition-all 
    duration-200
    focus:ring-2
    min-h-[48px]
    cursor-pointer
    ${error ? 'border-red-500' : ''}
    ${className}
  `.trim();

  const selectStyle = {
    borderColor: error ? '#EF4444' : form.input.border,
    ':focus': {
      borderColor: form.input.focus.border,
      '--tw-ring-color': form.input.focus.ring
    }
  };

  const dropdownClasses = `
    absolute 
    w-full 
    mt-1 
    bg-white 
    border 
    rounded-lg 
    shadow-lg 
    z-50 
    max-h-60 
    overflow-y-auto
  `.trim();

  const optionClasses = `
    px-4 
    py-2 
    hover:bg-gray-100 
    cursor-pointer 
    transition-colors
  `.trim();

  const labelClasses = `
    block 
    text-sm 
    font-semibold 
    uppercase 
    tracking-wide 
    mb-2
  `.trim();

  const labelStyle = {
    color: form.label.color
  };

  const errorClasses = `
    mt-1 
    text-sm 
    text-red-500
  `.trim();

  const selectedItemClasses = `
    inline-flex 
    items-center 
    bg-primary-100 
    text-primary-800 
    rounded-full 
    px-3 
    py-1 
    text-sm 
    mr-2 
    mb-2
  `.trim();

  const handleSelect = (option) => {
    const newValue = value.includes(option.value)
      ? value.filter(v => v !== option.value)
      : [...value, option.value];
    onChange(newValue);
  };

  const getSelectedLabels = () => {
    return value
      .map(v => options.find(o => o.value === v)?.label)
      .filter(Boolean);
  };

  const removeValue = (valueToRemove) => {
    onChange(value.filter(v => v !== valueToRemove));
  };

  return (
    <div ref={containerRef} className={containerClasses}>
      {label && (
        <label 
          className={labelClasses}
          style={labelStyle}
        >
          {label}
        </label>
      )}
      <div
        className={selectClasses}
        style={selectStyle}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap pt-1">
          {getSelectedLabels().map((label, index) => (
            <span key={index} className={selectedItemClasses}>
              {label}
              <button
                type="button"
                className="ml-1 hover:text-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(value[index]);
                }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          {value.length === 0 && (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
      </div>
      {isOpen && (
        <div className={dropdownClasses}>
          {options.map((option) => (
            <div
              key={option.value}
              className={optionClasses}
              onClick={() => handleSelect(option)}
            >
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => {}}
                className="mr-2"
              />
              {option.label}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p className={errorClasses}>{error}</p>
      )}
    </div>
  );
};

export default MultiSelect;
